import React, { useState, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  FileText,
  FolderOpen,
  User,
  LogOut,
  Menu,
  Calendar,
  Contact,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Plus,
  Search,
  KeyRound,
  BellRing,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { useAuthStore } from '@/store/authStore'
import AppSwitcher from './AppSwitcher'
import ProjectCentralSidebar from './ProjectCentralSidebar'
import AdminPanelSidebar from './AdminPanelSidebar'
import MarketingEmailSidebar from './MarketingEmailSidebar'
import NotificationBell from '../MarketingEmail/NotificationBell'
import TaskNotificationBell from '../ProjectCentral/TaskNotificationBell'
import ShortcutHelpDialog from '../ProjectCentral/ShortcutHelpDialog'
import TimerBar from '../ProjectCentral/TimerBar'
import GlobalTaskSearch from '../ProjectCentral/GlobalTaskSearch'
import CreateTaskDialog from '../ProjectCentral/CreateTaskDialog'
import { useKeyboardShortcuts } from '@/lib/useKeyboardShortcuts'

const AppLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const searchRef = useRef(null)

  useKeyboardShortcuts({
    onSearch: () => searchRef.current?.focus(),
    onCreateTask: () => setCreateOpen(true),
    onShowHelp: () => setShortcutsOpen(true),
  })
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuthStore()

  // Detect active app from route
  const isProjectCentral = location.pathname.startsWith('/projects')
  const isAdminPanel = location.pathname.startsWith('/admin-panel')
  const isMarketingEmail = location.pathname.startsWith('/marketing-email')
  const activeApp = isProjectCentral
    ? 'project-central'
    : isAdminPanel
    ? 'admin-panel'
    : isMarketingEmail
    ? 'marketing-email'
    : 'ems'
  const projectIdMatch = location.pathname.match(/^\/projects\/(\d+)/)
  const currentProjectId = projectIdMatch ? projectIdMatch[1] : undefined

  // Navigation groups matching shadcn style
  const navigationGroups = [
    {
      label: 'Home',
      items: [
        {
          key: '/dashboard',
          icon: LayoutDashboard,
          label: 'Dashboard',
        },
        ...(user?.role === 'admin' || user?.role === 'hr'
          ? [
              {
                key: '/employees',
                icon: Users,
                label: 'Employees',
              },
            ]
          : []),
        {
          key: '/team-directory',
          icon: Contact,
          label: 'Team',
        },
        {
          key: '/holidays',
          icon: Calendar,
          label: 'Holidays',
        },
        {
          key: '/leaves',
          icon: FileText,
          label: 'Leaves',
        },
      ],
    },
    {
      label: 'Documents',
      items: [
        {
          key: '/company-documents',
          icon: FolderOpen,
          label: 'Company Documents',
        },
      ],
    },
  ]

  const handleMenuClick = (key) => {
    navigate(key)
    setMobileOpen(false)
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const NavItem = ({ item, isMobile = false }) => {
    const Icon = item.icon
    const isActive = location.pathname === item.key
    
    return (
      <button
        onClick={() => handleMenuClick(item.key)}
        className={cn(
          "w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors",
          isActive
            ? "bg-gray-100 text-sidebar-foreground font-medium dark:bg-sidebar-accent dark:text-sidebar-accent-foreground"
            : "text-sidebar-foreground/70 hover:bg-gray-100 hover:text-sidebar-foreground dark:hover:bg-sidebar-accent dark:hover:text-sidebar-accent-foreground",
          collapsed && !isMobile && "justify-center px-2"
        )}
      >
        <Icon className="h-4 w-4 shrink-0" />
        {(!collapsed || isMobile) && <span>{item.label}</span>}
      </button>
    )
  }

  const SidebarContent = ({ isMobile = false }) => (
    <div className="flex flex-col h-full bg-sidebar">
      {/* App Switcher */}
      <div className={cn(
        "flex items-center h-14 border-b border-sidebar-border",
        collapsed && !isMobile ? "justify-center px-2" : "px-3"
      )}>
        <AppSwitcher activeApp={activeApp} collapsed={collapsed && !isMobile} />
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-2">
        <div className="px-2 space-y-4">
          {isProjectCentral ? (
            <ProjectCentralSidebar collapsed={collapsed && !isMobile} />
          ) : isAdminPanel ? (
            <AdminPanelSidebar collapsed={collapsed && !isMobile} />
          ) : isMarketingEmail ? (
            <MarketingEmailSidebar collapsed={collapsed && !isMobile} />
          ) : (
            navigationGroups.map((group, groupIndex) => (
              <div key={group.label}>
                {(!collapsed || isMobile) && (
                  <div className="px-3 mb-1">
                    <span className="text-xs font-medium text-sidebar-foreground/50">
                      {group.label}
                    </span>
                  </div>
                )}
                {collapsed && !isMobile && groupIndex > 0 && (
                  <Separator className="mb-2 bg-sidebar-border" />
                )}
                <nav className="space-y-0.5">
                  {group.items.map((item) => (
                    <NavItem key={item.key} item={item} isMobile={isMobile} />
                  ))}
                </nav>
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {/* User Profile */}
      <div className="border-t border-sidebar-border">
        <div className="px-2 py-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={cn(
                "w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-sidebar-accent transition-colors",
                collapsed && !isMobile && "justify-center"
              )}>
                <Avatar className="h-7 w-7 shrink-0">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {user?.employee?.first_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                {(!collapsed || isMobile) && (
                  <>
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium text-sidebar-foreground truncate">
                        {user?.employee?.first_name && user?.employee?.last_name
                          ? `${user.employee.first_name} ${user.employee.last_name}`
                          : user?.email?.split('@')[0]
                        }
                      </p>
                      <p className="text-xs text-sidebar-foreground/60 truncate">
                        {user?.email}
                      </p>
                    </div>
                    <MoreHorizontal className="h-4 w-4 shrink-0 text-sidebar-foreground/60" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/profile/api-tokens')}>
                <KeyRound className="mr-2 h-4 w-4" />
                API tokens & AI tools
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/profile/notifications')}>
                <BellRing className="mr-2 h-4 w-4" />
                Notification settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-40 h-screen border-r border-sidebar-border bg-sidebar transition-all duration-300 hidden lg:block",
          collapsed ? "w-16" : "w-64"
        )}
      >
        <SidebarContent />
        
        {/* Collapse Toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-[72px] bg-background border border-border rounded-full p-1 text-muted-foreground hover:text-foreground shadow-sm"
        >
          {collapsed ? (
            <ChevronRight className="h-3 w-3" />
          ) : (
            <ChevronLeft className="h-3 w-3" />
          )}
        </button>
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-64 p-0 bg-sidebar border-sidebar-border">
          <SidebarContent isMobile />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className={cn(
        "transition-all duration-300",
        collapsed ? "lg:ml-16" : "lg:ml-64"
      )}>
        {/* Header */}
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background px-4 lg:px-6">
          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>

          {/* Desktop header content */}
          {isProjectCentral ? (
            <div className="hidden lg:flex items-center gap-3 flex-1">
              <GlobalTaskSearch ref={searchRef} />
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-1.5" />
                Create
              </Button>
            </div>
          ) : (
            <div className="hidden lg:block" />
          )}

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* Mobile: search + create for Project Central */}
            {isProjectCentral && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden"
                  onClick={() => {/* Mobile search can be added later */}}
                >
                  <Search className="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden"
                  onClick={() => setCreateOpen(true)}
                >
                  <Plus className="h-5 w-5" />
                </Button>
              </>
            )}
            <TaskNotificationBell />
            {isMarketingEmail && <NotificationBell />}
            {/* Mobile only: show user avatar */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden">
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {user?.employee?.first_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profile/api-tokens')}>
                  <KeyRound className="mr-2 h-4 w-4" />
                  API tokens & AI tools
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profile/notifications')}>
                  <BellRing className="mr-2 h-4 w-4" />
                  Notification settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>

      {/* Global timer bar */}
      <TimerBar />

      {/* Global create task dialog */}
      {isProjectCentral && (
        <CreateTaskDialog open={createOpen} onOpenChange={setCreateOpen} defaultProjectId={currentProjectId} />
      )}
      {isProjectCentral && <ShortcutHelpDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />}
    </div>
  )
}

export default AppLayout
